import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import toast from "react-hot-toast";
import saleService from "../../../Services/saleService";
import doctorService from "../../../Services/doctorService";
import patientService from "../../../Services/patientService";
import PatientReport from "../PatientReport";
import { pdf } from "@react-pdf/renderer";

const Patients = () => {
  const navigate = useNavigate();

  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [patients, setPatients] = useState([]);
  const [patientsMaster, setPatientsMaster] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  // FETCH ALL DATA (SALES + DOCTORS + PATIENTS)
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        const [salesRes, doctorRes, patientRes] = await Promise.all([
          saleService.fetchAllSale(),
          doctorService.fetchAllDoctors(),
          patientService.fetchAllPatients(),
        ]);

        const sales = salesRes.sales || [];
        const doctorsData = doctorRes.doctors || [];
        const patientsData = patientRes.patients || [];

        setDoctors(doctorsData);
        setPatientsMaster(patientsData);

        // MERGE SALES + DOCTORS + PATIENTS
      const merged = sales.map((sale) => {
  const doctor = doctorsData.find((d) => d.id === sale.doc_id);
  const patient = patientsData.find((p) => p.patient_id === sale.patient_id);

  return {
    ...sale,
    doctor_name: doctor?.name || sale.doctor_name || "",
    pmdc_no: doctor?.pmdc_no || "", // <-- added this line
    qualification: doctor?.qualification || "",
    specialization: doctor?.specialization || "",
    full_name: patient?.full_name || sale.full_name || "",
    contact_number: patient?.contact_number || sale.contact_number || "",
    CNIC: patient?.CNIC || sale.CNIC || "",
    address: patient?.address || sale.address || "",
    age: patient?.age || sale.age || "",
    date_of_birth: patient?.date_of_birth || sale.date_of_birth || "",
    gender: patient?.gender || sale.gender || "",
    history: sale.history || "",
    diagnosis: sale.diagnosis || "",
  };
});

        setPatients(merged);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Failed to load data");
      }
    };

    fetchAllData();
  }, []);

  // FILTER BY SEARCH
  useEffect(() => {
    const filtered =
      patients.filter(
        (item) =>
          item?.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
          item?.CNIC?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchData, patients]);

  // PAGINATION
  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // PRINT PATIENT REPORT
const handlePrint = async (row) => {
  try {
    const patientPayload = {
      patient_name: row.full_name,
      doctor_name: row.doctor_name || "",
      pmdc_no: row.pmdc_no || "",  // <-- added this line
      patient_id: row.patient_id,
      sale_date: row.date,
      qualification: row.qualification || "",
      specialization: row.specialization || "",
      age: row.age || "",
      date_of_birth: row.date_of_birth || "",
      gender: row.gender || "",
      history: row.history || "",
      diagnosis: row.diagnosis || "",
    };

    const doc = <PatientReport patient={patientPayload} />;

    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);

    const newWindow = window.open(url, "_blank");

    if (newWindow) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
  } catch (error) {
    console.error(error);
    toast.error("Print failed");
  }
};
  return (
    <div className="p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-6 py-4 mb-5 shadow-sm">
        <div>
          <h1 className="text-[17px] font-[500] text-gray-800">
            Patients Table
          </h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {filteredData.length} records found
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <SearchIcon
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              style={{ fontSize: 16 }}
            />
            <input
              type="search"
              placeholder="Search by name or CNIC..."
              value={searchData}
              onChange={(e) => setSearchData(e.target.value)}
              className="pl-8 pr-3 py-[7px] w-[230px] text-[13px] bg-gray-50 border border-gray-200 rounded-lg"
            />
          </div>

          <Link to="AddEdit">
            <button className="bg-gray-800 hover:bg-gray-700 text-white text-[13px] px-4 py-[7px] rounded-lg">
              + Enter Patient
            </button>
          </Link>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-800 text-[11px] uppercase text-white">
              <th className="py-3 px-4 text-left">Sale No.</th>
              <th className="py-3 px-4 text-left">Patient No.</th>
              <th className="py-3 px-4 text-left">Check-up Date</th>
              <th className="py-3 px-4 text-left">Patient Name</th>
              <th className="py-3 px-4 text-left">Contact</th>
              <th className="py-3 px-4 text-center">CNIC</th>
              <th className="py-3 px-4 text-center">Address</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((row) => (
              <tr
                key={row.sale_id}
                className="border-t hover:bg-gray-50 text-[13px]"
              >
                <td className="py-3 px-4">{row.sale_id}</td>
                <td className="py-3 px-4">{row.patient_id}</td>
                <td className="py-3 px-4">{formatDate(row.date)}</td>
                <td className="py-3 px-4 font-[500]">{row.full_name}</td>
                <td className="py-3 px-4">{row.contact_number}</td>
                <td className="py-3 px-4 text-center">{row.CNIC}</td>
                <td className="py-3 px-4 text-center">{row.address}</td>
                <td className="py-3 px-4 text-center">
                  <button
                    onClick={() => handlePrint(row)}
                    className="bg-blue-50 text-blue-600 text-[12px] px-3 py-1 rounded-md hover:bg-blue-100"
                  >
                    Print
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default Patients;